import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, Modal, Spinner, Pagination } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  fetchEmergencyContacts,
  deleteEmergencyContact,
  selectContacts,
  selectMeta,
  selectLoading,
  selectError
} from '../../store/emergencyContact/emergencyContactSlice';
import { getEffectiveStudentId } from '../../utilities/teacherModulePermissions';
import { formatDisplayDate } from '../../utilities/displayFormat';

const EmergencyContactListComp = ({ data, viewMode }) => {
  // Always call hooks first
  const dispatch = useDispatch();
  const contacts = useSelector(selectContacts) || [];
  const meta = useSelector(selectMeta);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  let profileData = useSelector((state) => state.setting.setting.user_data);
  profileData = profileData ? JSON.parse(profileData) : null;
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    dispatch(fetchEmergencyContacts({ page: currentPage, limit: 10, student_id: getEffectiveStudentId(profileData) }));
  }, [dispatch, currentPage]);

  // Filter for active status if in profile view
  const filteredData = viewMode === 'profile' ? (Array.isArray(data) ? data.filter(item => item.status === 1) : []) : data;
  // If in profile view and no active data, show form instead of list
  if (viewMode === 'profile' && (!filteredData || filteredData.length === 0)) {
    return (
      <Container>
        <div className="text-center p-5">
          <p className="text-muted">No active Emergency Contact. Please add new data.</p>
          <Link to="/dashboard/emergency-contact/add">
            <Button variant="primary">Add Your First Contact</Button>
          </Link>
        </div>
      </Container>
    );
  }

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewDetails = (contact) => {
    setSelectedContact(contact);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedContact(null);
  };

  const handleDelete = async (contactId, contactName) => {
    const result = await Swal.fire({
      title: 'Delete Contact?',
      text: `Are you sure you want to delete "${contactName}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteEmergencyContact(contactId)).unwrap();
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Emergency contact has been deleted.',
          timer: 2000
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error || 'Failed to delete contact'
        });
      }
    }
  };

  const formatDate = formatDisplayDate;

  const renderPagination = () => {
    if (!meta || meta.last_page <= 1) return null;

    const items = [];
    for (let number = 1; number <= meta.last_page; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="justify-content-center mt-3">
        <Pagination.Prev
          key="prev"
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        />
        {items}
        <Pagination.Next
          key="next"
          disabled={currentPage === meta.last_page}
          onClick={() => handlePageChange(currentPage + 1)}
        />
      </Pagination>
    );
  };

  if (loading && (!contacts || contacts.length === 0)) {
    return (
      <Container>
        <div className="text-center py-5">
          <Spinner animation="border" role="status" style={{ color: '#6f42c1' }}>
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3 text-muted mb-0">Loading contacts...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
          <Card.Body className="text-center py-5">
            <i className="icon material-symbols-outlined" style={{ fontSize: '48px', color: '#dc3545' }}>error</i>
            <p className="text-danger mt-2 mb-3">{error}</p>
            <Button variant="primary" onClick={() => dispatch(fetchEmergencyContacts({ page: 1, limit: 10, student_id: getEffectiveStudentId(profileData) }))}>
              Retry
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="px-0">
      {/* Scoped styles matching the Allergy Action Plan / Lunch Plan
          Waiver / No Peanut Policy list screens - full-width cards (one
          per row, see the Col below) with a hover lift, so this screen
          stays visually consistent with the other 3. */}
      <style>{`
        .emergency-contact-card {
          border: 1px solid #eef0f3;
          border-radius: 12px;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .emergency-contact-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 24px rgba(17, 24, 39, 0.08) !important;
        }
      `}</style>
      {!Array.isArray(contacts) || contacts.length === 0 ? (
        <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
          <Card.Body className="text-center py-5">
            <i className="icon material-symbols-outlined" style={{ fontSize: '64px', color: '#ccc' }}>
              contact_emergency
            </i>
            <p className="mt-3 text-muted">No emergency contacts found. Add your first contact.</p>
            <Link to="/dashboard/emergency-contact/add">
              <Button variant="primary">Add Your First Contact</Button>
            </Link>
          </Card.Body>
        </Card>
      ) : (
        <Row className="g-3">
          {contacts.map((contact) => (
            <Col xs="12" key={contact.id}>
              <Card className="emergency-contact-card shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="d-flex align-items-center gap-3">
                      <h6 className="mb-0 fw-bold text-dark">
                        <i className="icon material-symbols-outlined me-2" style={{ verticalAlign: 'middle', fontSize: '20px' }}>person</i>
                        {contact.contact_name}
                      </h6>
                      <Badge
                        bg={contact.status === 1 ? "success" : "secondary"}
                        className="rounded-pill"
                        style={{ fontSize: '11px', fontWeight: 500, padding: '3px 10px', lineHeight: 1.3 }}
                      >
                        {contact.status === 1 ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <div className="d-flex align-items-center gap-3 flex-shrink-0">
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => handleViewDetails(contact)}
                        >
                          <i className="icon material-symbols-outlined me-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>visibility</i>
                          View
                        </Button>
                        <Link to={`/dashboard/emergency-contact/edit/${contact.id}`}>
                          <Button variant="primary" size="sm">
                            <i className="icon material-symbols-outlined me-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>edit</i>
                            Edit
                          </Button>
                        </Link>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(contact.id, contact.contact_name)}
                        >
                          <i className="icon material-symbols-outlined me-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>delete</i>
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                  <Row className="g-4">
                    <Col md="3">
                      <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        Relation
                      </div>
                      <div style={{ fontSize: '14px' }}>{contact.relation || 'N/A'}</div>
                    </Col>
                    <Col md="3">
                      <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        Phone
                      </div>
                      <div style={{ fontSize: '14px' }}>{contact.phone || 'N/A'}</div>
                    </Col>
                    <Col md="3">
                      <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        Added
                      </div>
                      <div style={{ fontSize: '14px' }}>
                        <i className="icon material-symbols-outlined me-1" style={{ fontSize: '14px', verticalAlign: 'middle' }}>calendar_today</i>
                        {formatDate(contact.created_at)}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
      {renderPagination()}

      {/* View Details Modal - size="xl" so the full-width sections below
          actually use the available space. */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            Emergency Contact #{selectedContact?.id} Details
            {selectedContact && (
              <Badge bg={selectedContact.status === 1 ? "success" : "secondary"} className="rounded-pill">
                {selectedContact.status === 1 ? "Active" : "Inactive"}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {selectedContact && (
            <div>
              <div className="mb-4 p-3" style={{ background: '#f8f9fa', border: '1px solid #eef0f3', borderRadius: '10px' }}>
                <div className="d-flex align-items-center mb-3">
                  <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', backgroundColor: '#6f42c1' }}>
                    <i className="icon material-symbols-outlined" style={{ fontSize: '18px' }}>person</i>
                  </div>
                  <h6 className="mb-0 fw-bold">Contact Information</h6>
                </div>
                <Row className="g-3">
                  <Col md="4">
                    <div className="text-muted" style={{ fontSize: '12px' }}>Contact Name</div>
                    <div style={{ fontSize: '14px' }}>{selectedContact.contact_name || 'N/A'}</div>
                  </Col>
                  <Col md="4">
                    <div className="text-muted" style={{ fontSize: '12px' }}>Relation</div>
                    <div style={{ fontSize: '14px' }}>{selectedContact.relation || 'N/A'}</div>
                  </Col>
                  <Col md="4">
                    <div className="text-muted" style={{ fontSize: '12px' }}>Phone Number</div>
                    <div style={{ fontSize: '14px' }}>{selectedContact.phone || 'N/A'}</div>
                  </Col>
                </Row>
              </div>

              <Row className="g-3">
                <Col md="6">
                  <div className="text-muted" style={{ fontSize: '12px' }}>Created</div>
                  <div style={{ fontSize: '14px' }}>{formatDate(selectedContact.created_at)}</div>
                </Col>
                <Col md="6">
                  <div className="text-muted" style={{ fontSize: '12px' }}>Last Updated</div>
                  <div style={{ fontSize: '14px' }}>{formatDate(selectedContact.updated_at)}</div>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          {selectedContact && (
            <Link to={`/dashboard/emergency-contact/edit/${selectedContact.id}`}>
              <Button variant="primary" onClick={handleCloseModal}>
                <i className="icon material-symbols-outlined me-1" style={{verticalAlign: 'middle'}}>edit</i>
                Edit
              </Button>
            </Link>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
export default EmergencyContactListComp;
